import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import { v4 as uuidv4 } from "uuid";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { slate, teal } from "../../../theme/colors";
import { docUploadModalOpen } from "../../../redux/actions/docUploadModalActions";
import { useUploadQueueOptional } from "../tabs/LoanDrilldownDocumentsTab/UploadQueueContext";

/**
 * Stages a FileList against one record: builds queue entries (same shape the
 * Documents tab's own dropzones build, so the queue cannot tell where the
 * files came from), pre-selects the record in the queue's picker and hands
 * them over. Outside an UploadQueueProvider it opens the upload modal locked
 * to the record instead. Shared by the dropzone and the panel's Add button.
 */
export const useStageRecordFiles = (assignTo) => {
  const dispatch = useDispatch();
  const queue = useUploadQueueOptional();

  return useCallback(
    (fileList) => {
      const entries = Array.from(fileList || []).map((file) => ({
        id: uuidv4(),
        file,
        name: file.name,
        file_display_name: file.name,
      }));
      if (!entries.length) return;
      if (queue) {
        // The user can still change the target in the queue before uploading.
        queue.setAssociationTarget(
          assignTo?.type && assignTo?.id
            ? { type: assignTo.type, id: assignTo.id }
            : null,
        );
        queue.onFilesAdded(entries);
        return;
      }
      // No provider around this surface: hand the entries to the modal so the
      // drop is not swallowed — its queue seeds from them.
      dispatch(docUploadModalOpen(assignTo, { initialFiles: entries }));
    },
    [queue, dispatch, assignTo],
  );
};

/**
 * The small dashed drop target beside one borrower's / property's documents
 * (ported from tpo-portal-frontend's RecordDocumentsPanel).
 *
 * Drop files on it (or click to browse) and they land in the enclosing tab's
 * bottom-docked upload queue (FixedUploadQueue), pre-assigned to this record —
 * the user picks document types there and clicks Upload. Nothing goes to the
 * server on drop. Outside an UploadQueueProvider the drop falls back to opening
 * the upload modal locked to the record.
 *
 * `hasDocuments=false` renders the taller empty-state copy; otherwise it is a
 * slim strip so the affordance doesn't disappear once the first file lands.
 *
 * @param {{ type: "borrower"|"property", id: string, label?: string }} assignTo
 */
const RecordDocumentsDropzone = ({
  assignTo,
  hasDocuments = false,
  // Custom body (the panel's empty-state copy + buttons). Replaces the
  // default copy; any control inside must stop propagation so its click
  // doesn't also open the file picker.
  children,
  sx,
}) => {
  const takeFiles = useStageRecordFiles(assignTo);
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => inputRef.current?.click();

  return (
    <Flx
      column
      center
      g={0.75}
      role="button"
      tabIndex={0}
      aria-label={`Upload documents${assignTo?.label ? ` for ${assignTo.label}` : ""}`}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        takeFiles(e.dataTransfer?.files);
      }}
      sx={{
        py: hasDocuments ? 1.25 : 3,
        px: 2,
        textAlign: "center",
        borderRadius: "10px",
        border: `2px dashed ${dragOver ? teal[400] : slate[300]}`,
        // Transparent at rest so it sits on whatever the rail's surface is;
        // only the drag/hover states paint a wash.
        backgroundColor: dragOver ? teal[50] : "transparent",
        cursor: "pointer",
        outline: "none",
        transition: "border-color .15s, background-color .15s",
        "&:hover, &:focus-visible": {
          borderColor: teal[300],
          backgroundColor: slate[50],
        },
        ...sx,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          takeFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {children ? (
        children
      ) : hasDocuments ? (
        <Txt component="div" sx={{ fontSize: 12, color: slate[500] }}>
          Drop files here or click to add more
        </Txt>
      ) : (
        <>
          <InsertDriveFileOutlined sx={{ fontSize: 22, color: slate[400] }} />
          <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
            No documents linked to this {assignTo?.type || "record"} yet.
          </Txt>
          <Txt component="div" sx={{ fontSize: 12, color: slate[400] }}>
            Drop files here or click to upload
            {assignTo?.label ? ` — they attach to ${assignTo.label}` : ""}
          </Txt>
        </>
      )}
    </Flx>
  );
};

export default RecordDocumentsDropzone;
